import React, {useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {flowSelector} from "../../../../../redux/slices/flowSlice";
import SvgSelector from "../../../../../components/SvgSelector/SvgSelector";
import {getDaysToExpiry} from "../../../../../utils/helpers/getDaysToExpiry";
import SetNotePopup from "../../../../../components/UiKit/Popups/SetNotePopup/SetNotePopup";
import SupportPopup from "../../../../../components/UiKit/Popups/SupportPopup/SupportPopup";
import ConfirmPopup from "../../../../../components/UiKit/Popups/ConfirmPopup/ConfirmPopup";
import GetLinksPopup from "../../../../../components/UiKit/Popups/GetLinksPopup/GetLinksPopup";
import MuiTooltip from "../../../../../components/UiKit/MuiTooltip/MuiTooltip";
import FlowTableGeoTooltip from "../../../../../components/UiKit/FlowTableGeoTooltip/FlowTableGeoTooltip";
import FlowTableAppTooltip from "../../../../../components/UiKit/ToolTips/FlowTableAppFieldTooltip/FlowTableAppTooltip";
import {getUniqueApplications} from "../../../../../utils/helpers/getUniqueApplications";
import StatusContent from "../../GetFlowStatusContent/GetFlowStatusContent";
import {globalDataSelector} from "../../../../../redux/slices/globalDataSlice";
import {authSelector} from "../../../../../redux/slices/authSlice";
import {
    DOMAIN_STATUS_IN_PROGRESS,
    DOMAIN_STATUS_WORKING, OWNER, OWNER_OR_TEAM_LEAD, ownerAndTeamLeadList, TEAM_LEAD, teamsHighLevelAccess,
} from "../../../../../utils/vars/staticVariables";
import s from "../../flows.module.scss";
import {subscriptionSelector} from "../../../../../redux/slices/subscriptionSlice";
import {getLanguage} from "../../../../../storage/switchLanguage";
import {user_role, user_token_email} from "../../../../../storage/user";
import AssignRecordToUserPopup
    from "../../../../../components/UiKit/Popups/AssignRecordToUserPopup/AssignRecordToUserPopup";

const ActiveTd = ({
                      settings,
                      id,
                      redirector,
                      domain,
                      status: flow_status,
                      notes,
                      index,
                      onDeleteHandler,
                      onDuplicateFlowHandler,
                      getTooltip,
                      editFlowName,
                      user_email,
                      assignFlowToTeamMemberHandler
                  }) => {

    const tooltipRef = useRef(null);
    const flowActionPopupRef = useRef(null);
    const toggleFlowActionButtonRef = useRef(null);

    const [isVisibleExpiringDomainTooltip, setIsVisibleExpiringDomainTooltip] = useState(false);
    const [activeRemovePopup, setActiveRemovePopup] = useState(false);
    const [copyLinksPopupIsActive, setCopyLinksPopup] = useState(false);
    const [activeCreateFlowPopup, setActiveCreateFlowPopup] = useState(false);
    const [activeAssignToUserPopup, setActiveAssignToUserPopup] = useState(false);
    const [flowActionsPopup, setFlowActionsPopup] = useState(false);
    const [isActiveSetNotePopup, setActiveSetNotePopup] = useState(false);
    const [activeFlowCreateLimitPopup, setActiveFlowCreateLimitPopup] =
        useState(false);

    const {geoList} = useSelector(globalDataSelector);
    const {flowsList, duplicateFlowPending, assignFlowToUserPending} = useSelector(flowSelector);
    const {isPartner} = useSelector(authSelector);
    const {fetchSubscriptionsPending, freeDomainsCount} = useSelector(subscriptionSelector);


    const {t} = useTranslation();

    const handleClickOutsideActionsPopup = (event) => {
        if (
            flowActionPopupRef.current &&
            !flowActionPopupRef.current.contains(event.target) &&
            toggleFlowActionButtonRef.current &&
            !toggleFlowActionButtonRef.current.contains(event.target)
        ) {
            setFlowActionsPopup(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutsideActionsPopup);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideActionsPopup);
        };
    }, []);

    const domain_name = domain?.address

    const {geo = [], status: settings_status = ""} = settings || {};

    const isCopyingLinksAllowed = flow_status === DOMAIN_STATUS_WORKING
    const isDomainAvailable = flow_status === DOMAIN_STATUS_WORKING || flow_status === DOMAIN_STATUS_IN_PROGRESS

    const toggleTooltip = () => setIsVisibleExpiringDomainTooltip(!isVisibleExpiringDomainTooltip);

    const tooltipEventHandlers = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        ? {onClick: toggleTooltip}
        : {
            onMouseEnter: () => setIsVisibleExpiringDomainTooltip(true),
            onMouseLeave: () => setIsVisibleExpiringDomainTooltip(false),
        };

    const uniqueApplications = getUniqueApplications(redirector)

    const daysToExpiry = useMemo(() => {
        return getDaysToExpiry(domain.expired_date);
    }, [domain?.expired_date]);

    return (
        <tr>
            <td className={s.noteTd}>
                <SetNotePopup
                    active={isActiveSetNotePopup}
                    setActive={setActiveSetNotePopup}
                    flowId={id}
                    title={t("flow_name")}
                    notes={notes}
                    icon={<SvgSelector id="note-popup-icon"/>}
                    onSetNoteHandler={(form) => editFlowName(form, setActiveSetNotePopup)}
                />
                <SupportPopup
                    icon={<SvgSelector id="popup-duplicate-icon"/>}
                    active={activeFlowCreateLimitPopup}
                    setActive={setActiveFlowCreateLimitPopup}
                    action={() => setActiveFlowCreateLimitPopup(false)}
                    title={t("duplicate_flow_title")}
                    description={t("duplicate_limit_message")}
                />
                <ConfirmPopup
                    icon={<SvgSelector id="popup-duplicate-icon"/>}
                    active={activeCreateFlowPopup}
                    setActive={setActiveCreateFlowPopup}
                    action={() => onDuplicateFlowHandler(id, domain_name, flowsList, setActiveFlowCreateLimitPopup)}
                    confirmBtnText={t("continue")}
                    title={t("duplicate_flow_title")}
                    description={
                        isPartner
                            ? `${t("duplicate_flow")} ${domain_name} ${t('notif_lowercase_flow')}?`
                            : getLanguage() === 'en'
                                ? `If you duplicate the flow, a ${freeDomainsCount > 0 ? '$0' : '$6'} fee will be deducted for the domain`
                                : `${t("duplicate_popup_content")} ${freeDomainsCount > 0 ? '$0' : '$6'} ${t('for_domain')}`
                    }
                    subDescription={t('expiring_domain_popup_warning')}
                />
                <ConfirmPopup
                    icon={<SvgSelector id="popup-remove-icon"/>}
                    active={activeRemovePopup}
                    setActive={setActiveRemovePopup}
                    action={() => onDeleteHandler(id, domain_name)}
                    confirmBtnText={t("button_remove")}
                    title={t("flow_remove_delete_title")}
                    description={t("remove_flow_popup_content")}
                />
                <AssignRecordToUserPopup
                    active={activeAssignToUserPopup}
                    setActive={setActiveAssignToUserPopup}
                    action={(user_id) => assignFlowToTeamMemberHandler({flow_id: id, user_id})}
                />
                <GetLinksPopup
                    redirectors={redirector}
                    active={copyLinksPopupIsActive}
                    domain={domain_name}
                    setActive={setCopyLinksPopup}
                />

                <div className={s.editNoteField}>
                    <button
                        className={s.noteTextBtn}
                        onClick={() => setActiveSetNotePopup(true)}
                    >
                        <div className={s.noteText}>{notes}</div>
                        <MuiTooltip title={notes}></MuiTooltip>
                        <SvgSelector id="edit-icon"/>
                    </button>
                </div>

            </td>
            <td>{t(flow_status)}</td>
            <td
                className={s.domainTd}
            >
                <div className={s.domainTdContent} style={!isDomainAvailable ? {color: '#717784'} : null}>
                    <span className={s.domainName}>{domain_name || ""}</span>
                    {
                        isDomainAvailable &&
                        daysToExpiry <= 14
                        && <button
                            {...tooltipEventHandlers}
                            className={`${s.tooltipButton} ${daysToExpiry > 1 ? s.orange : s.red}`}
                        >
                            <SvgSelector id="tooltip-btn"/>
                            {
                                isVisibleExpiringDomainTooltip && getTooltip(tooltipRef, index, daysToExpiry)
                            }
                        </button>
                    }
                </div>

            </td>

            <td
                style={{position: "relative"}}
                className={`${geo?.length > 0 ? s.geoTd : s.mocked}`}
            >
                <div className={s.tdContentContainer}>
                    <FlowTableGeoTooltip geos={geo} geosList={geoList}>
                        <div className={s.iterativeContentWrapper}>
                            {geo.map(({id, code}) => {
                                const foundFlag = geoList.find((item) => item.id === id)?.flag;

                                return (
                                    <span className={s.value} key={id}>
                    <span
                        className={s.iconWrapper}
                        style={{
                            backgroundImage: foundFlag
                                ? `url(${foundFlag})`
                                : "none",
                        }}
                    ></span>
                    <span>{code}</span>
                  </span>
                                );
                            })}
                        </div>
                    </FlowTableGeoTooltip>
                </div>
            </td>
            <td className={s.statusTd}>
                <div className={s.statusIndicator}>
                    <StatusContent status={settings_status}/>
                </div>
            </td>
            <td
                className={`${uniqueApplications?.length > 0 ? s.applicationTd : s.mocked}`}
            >
                <div className={s.tdContentContainer}>
                    <FlowTableAppTooltip uniqueApplications={uniqueApplications}>
                        <div className={s.applicationContentWrapper}>
                            {uniqueApplications.map(({name, icon}, index) => (
                                <span className={s.appValue} key={index}>
                  <span
                      className={s.iconWrapper}
                      style={{
                          backgroundImage: icon ? `url(${icon})` : "none",
                      }}
                  ></span>
                  <span>{name}</span>
                </span>
                            ))}
                        </div>
                    </FlowTableAppTooltip>
                </div>
            </td>
            {
                (teamsHighLevelAccess.includes(user_role) || user_role === TEAM_LEAD) && <td>{user_email}</td>
            }

            <td className={s.actionsTd}>
                {flowActionsPopup && (
                    <ul className={s.flowActionsPopup} ref={flowActionPopupRef}>
                        <li onClick={() => setFlowActionsPopup(false)}>
                            <a
                                href={`/stats/ios/clicks?domain=${domain_name}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <SvgSelector id="new-stats-icon"/>{" "}
                                <span>{t("go_to_clicks_stat_option")}</span>
                            </a>
                        </li>

                        {
                            (user_role === OWNER && user_email === user_token_email) &&
                            <li>
                                <button
                                    onClick={() => {
                                        setActiveAssignToUserPopup(true);
                                        setFlowActionsPopup(false);
                                    }}
                                    disabled={assignFlowToUserPending || fetchSubscriptionsPending}
                                >
                                    <SvgSelector id="assign-user-icon"/>
                                    <span>
                                          {t("assign_to_user")}
                                        </span>
                                </button>
                            </li>
                        }

                        {
                            (user_role === OWNER_OR_TEAM_LEAD && user_email === user_token_email) || user_role !== OWNER_OR_TEAM_LEAD ? (
                                <>
                                    <li>
                                        <button
                                            onClick={() => {
                                                setActiveCreateFlowPopup(true);
                                                setFlowActionsPopup(false);
                                            }}
                                            disabled={!settings || duplicateFlowPending || fetchSubscriptionsPending}
                                        >
                                            <SvgSelector id="duplicate"/>
                                            <span>
                                          {duplicateFlowPending
                                              ? t("duplicating")
                                              : t("duplicate_option")}
                                        </span>
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => {
                                                setActiveRemovePopup(true);
                                                setFlowActionsPopup(false);
                                            }}
                                        >
                                            <SvgSelector id="new-remove-icon"/>
                                            <span>{t("button_remove")}</span>
                                        </button>
                                    </li>
                                </>
                            ) : null

                        }
                    </ul>
                )}

                <div className={s.actionsWrapper}>
                    <div className={s.actionsContainer}>

                        <div className={s.actions}>
                            {
                                !isDomainAvailable && <button
                                    className={`${s.manageTableFillBtn} ${s.linksBtn} ${s.attentionBtn}`}
                                >
                                    <MuiTooltip title={t("pay_attention_to_the_flow_status")}>
                                        <SvgSelector id="attention-icon"/>
                                    </MuiTooltip>
                                </button>
                            }
                            <button
                                className={`${s.manageTableStrokeBtn} ${s.linksBtn}`}
                                onClick={() => setCopyLinksPopup(true)}
                                disabled={!isCopyingLinksAllowed}
                            >
                                <MuiTooltip
                                    title={isCopyingLinksAllowed ? t("get_links_tooltip") : t("getting_links_unavailable")}>
                                    <SvgSelector id="new-links-icon"/>
                                </MuiTooltip>
                            </button>

                            <Link
                                to={`/my-flows/edit/${id}`}
                                className={`${s.manageTableStrokeBtn} ${s.linksBtn}`}
                            >
                                <MuiTooltip title={t("edit_option")}>
                                    <SvgSelector id="old-edit-icon"/>
                                </MuiTooltip>
                            </Link>

                            <button
                                className={s.removeBtn}
                                onClick={() => setFlowActionsPopup(!flowActionsPopup)}
                                ref={toggleFlowActionButtonRef}
                            >
                                <MuiTooltip title={t('more')}>
                                    <SvgSelector id="flow-menu"/>
                                </MuiTooltip>
                            </button>
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
};

export default ActiveTd;